"use client";

import React, { type ReactNode, useEffect, useState, useCallback } from "react";
import ErrorFallback from "./ErrorFallback";

interface Props {
  children: ReactNode;
}

interface SentryModule {
  ErrorBoundary: React.ComponentType<any>;
  captureException: (error: Error, context?: any) => void;
}

// Type for Sentry Scope
interface SentryScope {
  setTag: (key: string, value: string) => void;
  setContext: (key: string, context: Record<string, any>) => void;
}

// Error Boundary integrated with Sentry
// Implemented according to: https://docs.sentry.io/platforms/javascript/guides/react/features/error-boundary/
const SentryErrorBoundary: React.FC<Props> = ({ children }) => {
  const [Sentry, setSentry] = useState<SentryModule | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    // Dynamic import @sentry/react (for Vite/React)
    const loadSentry = async (): Promise<void> => {
      try {
        const SentryModule = await import("@sentry/react");
        setSentry(SentryModule);
      } catch {
        console.warn("Sentry is not installed. Error tracking is disabled.");
      } finally {
        setIsLoaded(true);
      }
    };

    void loadSentry();
  }, []);

  // Callback when error occurs - to propagate error to state management or log
  const handleError = useCallback((error: Error, componentStack: string) => {
    console.error("Error caught by Sentry ErrorBoundary:", {
      error: error.message,
      componentStack,
    });
  }, []);

  // While loading Sentry, render children directly
  if (!isLoaded) {
    return <>{children}</>;
  }

  // If Sentry is available, use Sentry.ErrorBoundary with options from docs
  if (Sentry?.ErrorBoundary) {
    const { ErrorBoundary } = Sentry;

    return (
      <ErrorBoundary
        // Fallback function approach (recommended) - receives error, componentStack, resetError
        fallback={() => <ErrorFallback />}
        // Callback before sending error to Sentry - add tags and context
        beforeCapture={(scope: SentryScope) => {
          scope.setTag("error_boundary", "root");
          scope.setTag("location", "root_layout");
          scope.setContext("error_boundary_info", {
            timestamp: new Date().toISOString(),
            url: typeof window !== "undefined" ? window.location.href : "",
          });
        }}
        // Callback when error occurs
        onError={handleError}
      >
        {children}
      </ErrorBoundary>
    );
  }

  // Fallback: If Sentry is not available, render children directly
  return <>{children}</>;
};

export default SentryErrorBoundary;
